import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

// ── Filter Keys ───────────────────────────────────────────────────────────────

export type ProductStatus = "draft" | "published" | "archived";
export const ALL_STATUSES: ProductStatus[] = ["draft", "published", "archived"];

export const SUPPORTED_LOCALES = ["en", "fr", "de", "es", "it"] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

// URL query param names — centralised so syncing stays consistent
export const FILTER_URL_PARAMS = {
  status: "status",
  locale: "locale",
} as const;

// ── Store ─────────────────────────────────────────────────────────────────────

export const useGlobalFiltersStore = defineStore("globalFilters", () => {
  const router = useRouter();

  /**
   * Set to true once initFilters() has completed.
   * DefaultLayout blocks <RouterView> until this is true, preventing
   * feature pages from making API calls before filters are ready.
   */
  const isInitialized = ref(false);

  // ── Filter State ────────────────────────────────────────────────────────────

  /**
   * Product status filter.
   * Empty array = "All statuses" (no filter applied on the server side).
   */
  const selectedStatus = ref<ProductStatus[]>([]);

  /**
   * Active locale / language for product content.
   * Sent to every API request as a locale parameter.
   */
  const selectedLocale = ref<SupportedLocale>("en");

  // ── Getters ─────────────────────────────────────────────────────────────────

  const isAllStatusesSelected = computed(
    () => selectedStatus.value.length === 0,
  );

  /**
   * Ready-to-spread params for API calls.
   * Use: `api.get("/products", { params: { ...filtersStore.filterParams } })`
   */
  const filterParams = computed(() => ({
    ...(selectedStatus.value.length && { status: selectedStatus.value }),
    locale: selectedLocale.value,
  }));

  // ── Actions ─────────────────────────────────────────────────────────────────

  const setStatus = (statuses: ProductStatus[]) => {
    selectedStatus.value = statuses;
    syncToUrl();
  };

  const setLocale = (locale: SupportedLocale) => {
    selectedLocale.value = locale;
    syncToUrl();
  };

  /**
   * Syncs current filter values to the URL as query params.
   * Called automatically after every setter.
   */
  function syncToUrl(): void {
    const query = { ...router.currentRoute.value.query };

    if (selectedStatus.value.length) {
      query[FILTER_URL_PARAMS.status] = selectedStatus.value.join(",");
    }
    else {
      delete query[FILTER_URL_PARAMS.status];
    }

    query[FILTER_URL_PARAMS.locale] = selectedLocale.value;

    router.replace({ query }).catch(() => {
      // Ignore navigation-duplicated errors
    });
  }

  /**
   * Initialise filters from URL query params.
   * Must be called once after the user is authenticated and the router is ready.
   * Triggered from MasterLayout when `authStore.user` becomes truthy.
   */
  const initFilters = async (query: Record<string, string | undefined>) => {
    if (isInitialized.value) return;

    // Restore status from URL
    if (query[FILTER_URL_PARAMS.status]) {
      const parsed = query[FILTER_URL_PARAMS.status]!
        .split(",")
        .filter((s): s is ProductStatus => ALL_STATUSES.includes(s as ProductStatus));
      selectedStatus.value = parsed;
    }

    // Restore locale from URL
    if (
      query[FILTER_URL_PARAMS.locale]
      && SUPPORTED_LOCALES.includes(query[FILTER_URL_PARAMS.locale] as SupportedLocale)
    ) {
      selectedLocale.value = query[FILTER_URL_PARAMS.locale] as SupportedLocale;
    }

    // Mark ready — DefaultLayout will now render <RouterView>
    isInitialized.value = true;
  };

  const resetFilters = () => {
    selectedStatus.value = [];
    selectedLocale.value = "en";
    syncToUrl();
  };

  return {
    // State
    isInitialized,
    selectedStatus,
    selectedLocale,

    // Getters
    isAllStatusesSelected,
    filterParams,

    // Actions
    setStatus,
    setLocale,
    initFilters,
    resetFilters,
  };
});
